import React from 'react'
import { useTranslation } from 'react-i18next'

interface Passkey {
  id: number
  deviceName: string
  createdAt: string
  lastUsedAt?: string
  backupEligible?: boolean
}

interface PasskeysCardProps {
  passkeys: Passkey[]
  loading: boolean
  registering: boolean
  newPasskeyName: string
  onNameChange: (name: string) => void
  onRegister: () => void
  onDelete: (id: number, name: string) => void
}

export const PasskeysCard: React.FC<PasskeysCardProps> = ({
  passkeys,
  loading,
  registering,
  newPasskeyName,
  onNameChange,
  onRegister,
  onDelete
}) => {
  const { t } = useTranslation()
  
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('security.passkeys')}
          </h3>
          {passkeys.length > 0 && (
            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
              {passkeys.length} {t('securityFeatureCards.active')}
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('securityFeatureCards.passkeysDescription')}
      </p>

      {/* Add New Passkey */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Add New Passkey
        </h4>
        <div className="flex gap-3">
          <input
            type="text"
            value={newPasskeyName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Device name (optional, e.g., 'My iPhone')"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          <button
            onClick={onRegister}
            disabled={registering}
            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {registering ? 'Adding...' : 'Add Passkey'}
          </button>
        </div>
      </div>

      {/* Existing Passkeys */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Your Passkeys
        </h4>
        {loading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        ) : passkeys.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
            <svg className="mx-auto h-10 w-10 mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <p>No passkeys yet</p>
            <p className="text-xs mt-1">Add your first passkey for secure authentication</p>
          </div>
        ) : (
          <div className="space-y-2">
            {passkeys.map((passkey) => (
              <div key={passkey.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {passkey.deviceName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Created: {new Date(passkey.createdAt).toLocaleDateString()}
                      {passkey.lastUsedAt && (
                        <> • Last used: {new Date(passkey.lastUsedAt).toLocaleDateString()}</>
                      )}
                    </div>
                    {passkey.backupEligible && (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Cloud Synced
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onDelete(passkey.id, passkey.deviceName)}
                  className="px-3 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface TwoFactorCardProps {
  enabled: boolean
  loading: boolean
  setupStep: 'idle' | 'setup' | 'verify'
  secret: string
  qrCode: string
  token: string
  backupCodes: string[]
  onTokenChange: (token: string) => void
  onBeginSetup: () => void
  onVerify: () => void
  onDisable: () => void
  onComplete: () => void
}

export const TwoFactorCard: React.FC<TwoFactorCardProps> = ({
  enabled,
  loading,
  setupStep,
  secret,
  qrCode,
  token,
  backupCodes,
  onTokenChange,
  onBeginSetup,
  onVerify,
  onDisable,
  onComplete
}) => {
  const { t } = useTranslation()
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('securityFeatureCards.twoFactorAuth')}
          </h3>
          {enabled && (
            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
              {t('securityFeatureCards.enabled')}
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('securityFeatureCards.twoFactorAuthDescription')}
      </p>

      {/* Setup Steps */}
      {setupStep === 'idle' && !enabled && (
        <div className="flex justify-end">
          <button
            onClick={onBeginSetup}
            disabled={loading}
            className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
          >
            {loading ? t('securityFeatureCards.settingUp') : t('securityFeatureCards.enable2FA')}
          </button>
        </div>
      )}

      {setupStep === 'setup' && (
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">
              {t('securityFeatureCards.step1ScanQR')}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
              {t('securityFeatureCards.openAuthAppScan')}
            </p>
            {qrCode && (
              <div className="bg-white p-4 rounded-lg inline-block">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
              </div>
            )}
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-3">
              Or manually enter this secret key:
            </p>
            <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600 font-mono text-sm break-all flex items-center justify-between">
              <span>{secret}</span>
              <button
                onClick={() => copyToClipboard(secret)}
                className="ml-2 p-1 text-gray-900 hover:text-blue-700"
                title="Copy"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Step 2: Verify Code
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Enter the 6-digit code from your authenticator app:
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={token}
                onChange={(e) => onTokenChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
                maxLength={6}
              />
              <button
                onClick={onVerify}
                disabled={loading || token.length !== 6}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {setupStep === 'verify' && backupCodes.length > 0 && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-800 dark:text-green-200 font-semibold">
                2FA Enabled Successfully!
              </p>
            </div>
            <p className="text-xs text-green-700 dark:text-green-300 mb-3">
              Save these backup codes in a secure place. You can use them to access your account if you lose access to your authenticator app.
            </p>
            <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600">
              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {backupCodes.map((code, idx) => (
                  <div key={idx} className="text-gray-900 dark:text-white">
                    {code}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => {
                  const codesText = backupCodes.join('\n')
                  copyToClipboard(codesText)
                }}
                className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Copy Codes
              </button>
              <button
                onClick={onComplete}
                className="flex-1 px-3 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {enabled && setupStep === 'idle' && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                  {t('securityFeatureCards.twoFactorActive')}
                </h4>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={onDisable}
              disabled={loading}
              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-600 dark:border-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
            >
              {loading ? t('securityFeatureCards.disabling') : t('securityFeatureCards.disable')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// SMS 2FA Card Component
interface SMS2FACardProps {
  enabled: boolean
  loading: boolean
  phone: string | null
  setupStep: 'idle' | 'phone' | 'verify' | 'disable'
  phoneNumber: string
  verificationCode: string
  resendTimer: number
  onPhoneChange: (phone: string) => void
  onCodeChange: (code: string) => void
  onSendCode: () => void
  onVerify: () => void
  onResend: () => void
  onDisable: (password: string) => void
  onCancel: () => void
}

export const SMS2FACard: React.FC<SMS2FACardProps> = ({
  enabled,
  loading,
  phone,
  setupStep,
  phoneNumber,
  verificationCode,
  resendTimer,
  onPhoneChange,
  onCodeChange,
  onSendCode,
  onVerify,
  onResend,
  onDisable,
  onCancel
}) => {
  const { t } = useTranslation()
  const [disablePassword, setDisablePassword] = React.useState('')
  const [showDisableForm, setShowDisableForm] = React.useState(false)

  const handleDisableSubmit = () => {
    if (disablePassword) {
      onDisable(disablePassword)
      setDisablePassword('')
      setShowDisableForm(false)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('securityFeatureCards.sms2FA')}
          </h3>
          {enabled && (
            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
              {t('securityFeatureCards.enabled')}
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('securityFeatureCards.sms2FADescription')}
      </p>

      {/* Setup Steps */}
      {setupStep === 'idle' && !enabled && (
        <div className="flex justify-end">
          <button
            onClick={() => onSendCode()}
            disabled={loading}
            className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
          >
            {loading ? t('securityFeatureCards.settingUp') : t('securityFeatureCards.enableSMS2FA')}
          </button>
        </div>
      )}

      {setupStep === 'phone' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('securityFeatureCards.phoneNumber')}
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder={t('securityFeatureCards.phoneNumberPlaceholder')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('securityFeatureCards.phoneNumberHelper')}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={onSendCode}
              disabled={loading || !phoneNumber}
              className="flex-1 px-3 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
            >
              {loading ? t('securityFeatureCards.settingUp') : t('securityFeatureCards.sendVerificationCode')}
            </button>
          </div>
        </div>
      )}

      {setupStep === 'verify' && (
        <div className="space-y-4">
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-sm text-green-800 dark:text-green-200">
              {t('securityFeatureCards.verificationCodeSent')}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('securityFeatureCards.enterSMSCode')}
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('securityFeatureCards.codePlaceholder')}
              maxLength={6}
              className="w-full px-3 py-2 text-center text-lg tracking-widest font-mono border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
              autoFocus
            />
          </div>
          <div className="text-center">
            <button
              onClick={onResend}
              disabled={resendTimer > 0}
              className="text-sm text-gray-900 dark:text-gray-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendTimer > 0 
                ? t('securityFeatureCards.resendCodeIn', { seconds: resendTimer })
                : t('securityFeatureCards.resendCode')
              }
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={onVerify}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1 px-3 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
            >
              {loading ? t('securityFeatureCards.settingUp') : t('securityFeatureCards.verifyAndEnable')}
            </button>
          </div>
        </div>
      )}

      {/* Enabled State */}
      {setupStep === 'idle' && enabled && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                  {t('securityFeatureCards.sms2FAActive')}
                </h4>
                {phone && (
                  <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                    {t('securityFeatureCards.registeredPhone', { phone })}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            {!showDisableForm ? (
              <button
                onClick={() => setShowDisableForm(true)}
                disabled={loading}
                className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-600 dark:border-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
              >
                {t('securityFeatureCards.disableSMS2FA')}
              </button>
            ) : (
              <div className="w-full space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('securityFeatureCards.confirmPasswordToDisable')}
                  </label>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder={t('securityFeatureCards.passwordRequired')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowDisableForm(false)
                      setDisablePassword('')
                    }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleDisableSubmit}
                    disabled={loading || !disablePassword}
                    className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
                  >
                    {loading ? t('securityFeatureCards.disabling') : t('securityFeatureCards.disable')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
